/**
 * The window: a top bar, rail · list · pane, and a status strip.
 *
 * `relay/docs/architecture.md` is the reference and the desktop app is the
 * picture: a title bar across the top, an icon rail switching surfaces, a list
 * that belongs to the surface, a conversation that is header, transcript and
 * composer, and a strip along the bottom saying who you are signed in as. The
 * three regions are panels at `--rl-radius-region` on the tinted app ground,
 * `--rl-gutter` apart — the design system's § App chrome, which is also what
 * `theme.cpp` paints.
 *
 * ── What the browser cannot have ────────────────────────────────────────────
 * The desktop title bar also holds the window buttons and a File/Edit/View
 * menu bar. A tab has neither: the buttons belong to the browser, and every
 * menu entry already exists as a control on the surface it acts on. The search
 * field is missing for a different reason — it opens the command palette in the
 * app and this one has no palette, and a search box that searches nothing is
 * worse than a gap.
 *
 * ── Under 768px the list and the pane are two pages ─────────────────────────
 * Not two narrow columns. A 320px transcript is a column of two words, so only
 * one of the two is rendered and the rail becomes a bottom bar. Which one shows
 * is decided by the route — `/` is the list, `/c/:id` is the pane — so the back
 * button is the browser's own and the phone's back gesture does the right thing
 * without anything here listening for it.
 */

import React, { useEffect, useState } from 'react';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { useT } from '../i18n';
import { useStore } from '../lib/relay/store';
import { requestChrome } from '../state/chrome';

const NARROW = 768;

/** Under `width` pixels wide: 768 unless a surface needs more room. */
export function useNarrow(width = NARROW): boolean {
  const [narrow, setNarrow] = useState(() => window.innerWidth < width);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${width - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [width]);
  return narrow;
}

export type Surface = 'chats' | 'meetings' | 'boards' | 'settings';

interface RailItem {
  surface: Surface;
  to: string;
  icon: string;
  key: string;
}

/**
 * Four surfaces, because four are built.
 *
 * The desktop rail is chats · calendar · meetings · files · notes, and the
 * three missing here are real surfaces there with nothing behind them in this
 * app yet. A rail item that goes nowhere is worse than one that is absent.
 * Settings sits apart at the foot of the rail, as it does in the app.
 */
const RAIL: RailItem[] = [
  { surface: 'chats', to: '/', icon: 'RailChats', key: 'chrome.rail.chats' },
  { surface: 'meetings', to: '/meetings', icon: 'RailMeet', key: 'chrome.rail.meetings' },
  { surface: 'boards', to: '/boards', icon: 'RailBoards', key: 'chrome.rail.boards' }
];

const SETTINGS: RailItem = {
  surface: 'settings',
  to: '/settings',
  icon: 'Settings',
  key: 'chrome.rail.settings'
};

function RailButton({ item, active }: { item: RailItem; active: boolean }) {
  const { Icon } = ds();
  const t = useT();
  return (
    <Link to={item.to} title={t(item.key)} aria-label={t(item.key)} className="rl-rail-item" data-active={active}>
      <Icon name={item.icon} size={22} />
    </Link>
  );
}

function Rail({ active, orientation }: { active: Surface; orientation: 'column' | 'row' }) {
  const column = orientation === 'column';
  const items = [...RAIL, SETTINGS];

  if (!column) {
    return (
      <nav className="rl-rail rl-rail-row">
        {items.map((item) => (
          <RailButton key={item.surface} item={item} active={item.surface === active} />
        ))}
      </nav>
    );
  }

  return (
    <nav className="mv-panel mv-panel-chrome rl-rail">
      {/* No wordmark here: the rail is 72px and "mauvely." at the smallest size
          the design system draws it is wider than that. It sits in the top bar,
          which is where the app puts it too. */}
      {RAIL.map((item) => (
        <RailButton key={item.surface} item={item} active={item.surface === active} />
      ))}
      <div className="rl-rail-spacer" />
      <RailButton item={SETTINGS} active={active === 'settings'} />
    </nav>
  );
}

/** The title bar's right-hand end: who you are, and the two things the app
 *  offers from anywhere. */
function TopBar() {
  const { Button, Wordmark } = ds();
  const t = useT();
  const me = useStore((s) => s.me());
  const { navigate } = useRouter();

  return (
    <header className="rl-topbar">
      <Link to="/" className="rl-topbar-brand" aria-label="Relay">
        <Wordmark size={16} />
      </Link>

      <div className="rl-topbar-actions">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            navigate('/meetings');
            requestChrome('new-meeting');
          }}
        >
          {t('chrome.meetnow')}
        </Button>
        <Button
          variant="soft"
          size="sm"
          onClick={() => {
            navigate('/');
            requestChrome('new-chat');
          }}
        >
          {t('chrome.newchat')}
        </Button>
        {me && (
          <span className="rl-account" title={me.name}>
            <span className="rl-account-disc" aria-hidden="true">
              {initials(me.name)}
            </span>
            <span className="rl-account-name">{me.name}</span>
          </span>
        )}
      </div>
    </header>
  );
}

/** Who you are signed in as, and whether the socket is up. The one strip that
 *  carries state living nowhere else — never a count, never a spec. */
function StatusStrip() {
  const { Icon } = ds();
  const t = useT();
  const me = useStore((s) => s.me());
  // `'online'` is the socket's word for it (`lib/relay/socket.ts`), and the
  // strip said "Connecting…" under a perfectly good socket for as long as this
  // compared against `'open'`.
  const connection = useStore(
    (s) => (s as { connection?: () => string }).connection?.() ?? 'online'
  );
  const online = connection === 'online';

  return (
    <footer className="rl-status">
      <Icon name={online ? 'Connected' : 'Syncing'} size={13} />
      <span>
        {online && me
          ? t('chrome.status.connected', { name: me.name })
          : t('chrome.status.connecting')}
      </span>
    </footer>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Sample data says so, on every screen. A mockup that does not announce itself
 *  is a build somebody runs for a week without noticing. */
function SampleStrip() {
  const t = useT();
  const sample = useStore((s) => (s as { sample?: boolean }).sample === true);
  if (!sample) return null;

  return (
    <div
      style={{
        padding: 'var(--space-6) var(--space-16)',
        background: 'var(--warning-100)',
        color: 'var(--warning-600)',
        fontFamily: 'var(--font-mono), monospace',
        fontSize: 11,
        letterSpacing: 'var(--ls-label, 0.16em)',
        textTransform: 'uppercase'
      }}
    >
      {t('chrome.sample')}
    </div>
  );
}

export function Shell({
  surface,
  list,
  pane
}: {
  surface: Surface;
  /** The middle column. Absent for a surface that is one full-width page. */
  list?: React.ReactNode;
  pane: React.ReactNode;
}) {
  const narrow = useNarrow();
  const { path } = useRouter();

  // On a phone, the pane shows for any route deeper than the surface's own.
  const showPane = !list || path !== surfaceRoot(surface);

  if (narrow) {
    return (
      <div className="mv-app mv-window" data-narrow="true">
        <TopBar />
        <div className="mv-shell" data-narrow="true">
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SampleStrip />
            <div className="mv-scroll" style={{ flex: 1, background: 'var(--app-panel)' }}>
              {showPane ? pane : list}
            </div>
          </div>
        </div>
        <Rail active={surface} orientation="row" />
      </div>
    );
  }

  return (
    <div className="mv-app mv-window">
      <TopBar />
      <div
        className="mv-shell"
        style={list ? undefined : { gridTemplateColumns: 'var(--app-rail) 1fr' }}
      >
        <Rail active={surface} orientation="column" />
        {list && <section className="mv-panel">{list}</section>}
        <section className="mv-panel" style={{ display: 'flex', flexDirection: 'column' }}>
          <SampleStrip />
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {pane}
          </div>
        </section>
      </div>
      <StatusStrip />
    </div>
  );
}

function surfaceRoot(surface: Surface): string {
  return surface === 'chats' ? '/' : `/${surface}`;
}

/**
 * A panel's own header strip.
 *
 * `lead` is a face or a glyph beside the name, and it is a prop rather than
 * part of `title` because of where the line under the name has to start. An
 * avatar inside the title puts the whole title block's left edge at the
 * avatar's, so "Online" lined up under the picture instead of under the name —
 * the desktop app draws the avatar beside a column, and so does this.
 */
export function PanelHeader({
  lead,
  title,
  subtitle,
  actions
}: {
  lead?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <header className="mv-strip rl-head">
      <div className="rl-head-main">
        {lead}
        <div style={{ minWidth: 0 }}>
          <div className="rl-head-title">{title}</div>
          {subtitle !== undefined && subtitle !== '' && (
            <div className="rl-head-sub">{subtitle}</div>
          )}
        </div>
      </div>
      {actions && <div className="rl-head-actions">{actions}</div>}
    </header>
  );
}

/** An empty state: what is not here, then the next action. Never a definition
 *  of the feature — the design system's content rules are explicit about it. */
export function Empty({ title, body, action }: { title: string; body?: string; action?: React.ReactNode }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-40)',
        textAlign: 'center'
      }}
    >
      <div style={{ maxWidth: 360 }}>
        <div style={{ fontFamily: 'var(--font-display), sans-serif', fontWeight: 600, fontSize: 20 }}>
          {title}
        </div>
        {body && (
          <p style={{ color: 'var(--text-muted)', marginTop: 'var(--space-8)' }}>{body}</p>
        )}
        {action && <div style={{ marginTop: 'var(--space-16)' }}>{action}</div>}
      </div>
    </div>
  );
}
